import { offlineDb, type OfflineDb } from '@/lib/offline/offlineDb'
import type { EventRecord } from './types'

export type CachedEvents = Readonly<{
  events: EventRecord[]
  cachedAt: string
}>

export interface EventReadCache {
  read(userId: string): Promise<CachedEvents | undefined>
  save(userId: string, events: EventRecord[], cachedAt?: string): Promise<void>
  upsert(userId: string, event: EventRecord, cachedAt?: string): Promise<void>
  remove(userId: string, eventId: string, cachedAt?: string): Promise<void>
}

/** A small adapter around the shared, environment-isolated offline database. */
export function createEventReadCache(db: OfflineDb = offlineDb): EventReadCache {
  return {
    async read(userId) {
      const snapshot = await db.eventSnapshots.get(userId)
      return snapshot && { events: snapshot.events, cachedAt: snapshot.cachedAt }
    },
    async save(userId, events, cachedAt = new Date().toISOString()) {
      await db.eventSnapshots.put({ userId, events, cachedAt })
    },
    async upsert(userId, event, cachedAt = new Date().toISOString()) {
      const previous = await db.eventSnapshots.get(userId)
      const events = previous
        ? previous.events.map((item) => item.id === event.id ? event : item)
        : [event]
      if (previous && !previous.events.some((item) => item.id === event.id)) events.unshift(event)
      await db.eventSnapshots.put({ userId, events, cachedAt })
    },
    async remove(userId, eventId, cachedAt = new Date().toISOString()) {
      const previous = await db.eventSnapshots.get(userId)
      if (!previous) return
      await db.eventSnapshots.put({ userId, events: previous.events.filter((item) => item.id !== eventId), cachedAt })
    },
  }
}
